"use client";

import { c as _c } from "react/compiler-runtime";
import { useDepsChanged } from "@/hooks/useDepsChanged";
import { Button } from "@repo/blacksmith-ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/blacksmith-ui/popover";
import { dateRangeAtom } from "@/hooks/useConnectedGithubOrgs";
import { cn } from "@repo/blacksmith-ui/cn";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { recentDateRangeAtom } from "@/atoms/runsFilterAtoms";
export function MetricsDateRangePicker(t0) {
  const $ = _c(63);
  let buttonRef;
  let className;
  let isActive;
  let onSelect;
  let props;
  let source;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      onSelect,
      enableTimeSelection: t1,
      source,
      buttonRef,
      isActive,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = buttonRef;
    $[2] = className;
    $[3] = isActive;
    $[4] = onSelect;
    $[5] = props;
    $[6] = source;
    $[7] = t1;
  } else {
    buttonRef = $[1];
    className = $[2];
    isActive = $[3];
    onSelect = $[4];
    props = $[5];
    source = $[6];
    t1 = $[7];
  }
  const enableTimeSelection = t1 === undefined ? false : t1;
  const [dateRange, setDateRange] = useAtom(source === "recent_range" ? recentDateRangeAtom : dateRangeAtom);
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  let t2;
  if ($[8] !== dateRange) {
    t2 = [dateRange];
    $[8] = dateRange;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  if (useDepsChanged(t2)) {
    if (dateRange?.from) {
      const fromDateTime = DateTime.fromJSDate(dateRange.from, {
        zone: "utc"
      });
      setFromTime(fromDateTime.toFormat("HH:mm"));
    }
    if (dateRange?.to) {
      const toDateTime = DateTime.fromJSDate(dateRange.to, {
        zone: "utc"
      });
      setToTime(toDateTime.toFormat("HH:mm"));
    }
  }
  let t3;
  if ($[10] !== dateRange) {
    t3 = dateRange?.from ? DateTime.fromJSDate(dateRange.from, {
      zone: "utc"
    }).setZone("local", {
      keepLocalTime: true
    }).toJSDate() : undefined;
    $[10] = dateRange;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== dateRange) {
    t4 = dateRange?.to ? DateTime.fromJSDate(dateRange.to, {
      zone: "utc"
    }).setZone("local", {
      keepLocalTime: true
    }).toJSDate() : undefined;
    $[12] = dateRange;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = {
      from: t3,
      to: t4
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const localDisplayRange = t5;
  let t6;
  if ($[17] !== dateRange || $[18] !== enableTimeSelection || $[19] !== fromTime || $[20] !== onSelect || $[21] !== setDateRange || $[22] !== toTime) {
    t6 = (newDateRange, newFromTime, newToTime) => {
      const baseRange = newDateRange || dateRange;
      if (!baseRange) {
        return;
      }
      const fromTimeValue = newFromTime || fromTime;
      const toTimeValue = newToTime || toTime;
      const finalRange = {
        ...baseRange
      };
      if (enableTimeSelection) {
        if (finalRange.from) {
          const [fromHours, fromMinutes] = fromTimeValue.split(":").map(Number);
          finalRange.from = DateTime.fromJSDate(finalRange.from, {
            zone: "utc"
          }).set({
            hour: fromHours,
            minute: fromMinutes
          }).startOf("minute").toJSDate();
        }
        if (finalRange.from && !finalRange.to) {
          finalRange.to = new Date(finalRange.from);
        }
        if (finalRange.to) {
          const [toHours, toMinutes] = toTimeValue.split(":").map(Number);
          finalRange.to = DateTime.fromJSDate(finalRange.to, {
            zone: "utc"
          }).set({
            hour: toHours,
            minute: toMinutes
          }).endOf("minute").toJSDate();
        }
      }
      setDateRange(finalRange);
      onSelect?.(finalRange);
    };
    $[17] = dateRange;
    $[18] = enableTimeSelection;
    $[19] = fromTime;
    $[20] = onSelect;
    $[21] = setDateRange;
    $[22] = toTime;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const handleDateTimeChange = t6;
  let t7;
  if ($[24] !== handleDateTimeChange) {
    t7 = newLocalRange => {
      if (!newLocalRange) {
        return;
      }
      const utcRange = {
        from: newLocalRange.from ? DateTime.fromJSDate(newLocalRange.from).setZone("utc", {
          keepLocalTime: true
        }).startOf("day").toJSDate() : undefined,
        to: newLocalRange.to ? DateTime.fromJSDate(newLocalRange.to).setZone("utc", {
          keepLocalTime: true
        }).endOf("day").toJSDate() : undefined
      };
      handleDateTimeChange(utcRange);
    };
    $[24] = handleDateTimeChange;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleSelect = t7;
  let t8;
  if ($[26] !== dateRange || $[27] !== handleDateTimeChange) {
    t8 = times => {
      setFromTime(times.fromTime);
      setToTime(times.toTime);
      if (dateRange) {
        handleDateTimeChange(undefined, times.fromTime, times.toTime);
      }
    };
    $[26] = dateRange;
    $[27] = handleDateTimeChange;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleTimeChange = t8;
  let t9;
  if ($[29] !== dateRange || $[30] !== enableTimeSelection) {
    t9 = () => {
      if (!dateRange?.from) {
        return "Pick a date";
      }
      const formatDate = date => {
        const dt = DateTime.fromJSDate(date, {
          zone: "utc"
        });
        if (enableTimeSelection) {
          return dt.toFormat("MMM d, h:mm a");
        }
        return dt.toFormat("MMM d, yyyy");
      };
      const fromStr = formatDate(dateRange.from);
      const toStr = dateRange.to ? ` - ${formatDate(dateRange.to)}` : "";
      return `${fromStr}${toStr}${enableTimeSelection ? " UTC" : ""}`;
    };
    $[29] = dateRange;
    $[30] = enableTimeSelection;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const getFormattedDateRange = t9;
  let t10;
  if ($[32] !== className) {
    t10 = cn("grid gap-2", className);
    $[32] = className;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const t11 = isActive ? "text-[#161718] dark:text-white font-medium" : "text-neutral-900 dark:text-neutral-200/80 font-normal";
  const t12 = !dateRange && "text-neutral-500 dark:text-neutral-400";
  let t13;
  if ($[34] !== t11 || $[35] !== t12) {
    t13 = cn("relative z-10 justify-start font-normal cursor-pointer h-[22px] pl-2 pr-1.5 text-xs leading-4 rounded-md min-w-[182px]", "bg-transparent border-transparent", t11, "hover:text-neutral-600 dark:hover:text-neutral-300", "hover:bg-transparent", t12);
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CalendarIcon className="mr-2 size-4" />;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const t15 = getFormattedDateRange();
  let t16;
  if ($[38] !== buttonRef || $[39] !== t13 || $[40] !== t15) {
    t16 = <PopoverTrigger asChild={true}><Button ref={buttonRef} id="date" variant="subtle" className={t13}>{t14}{t15}</Button></PopoverTrigger>;
    $[38] = buttonRef;
    $[39] = t13;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const t17 = localDisplayRange?.from;
  let t18;
  if ($[42] !== source) {
    t18 = DateTime.now().startOf("day").minus(source === "recent_range" ? {
      months: 3
    } : {
      years: 1
    }).toJSDate();
    $[42] = source;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = DateTime.now().endOf("day").toJSDate();
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== fromTime || $[46] !== toTime) {
    t20 = {
      fromTime,
      toTime
    };
    $[45] = fromTime;
    $[46] = toTime;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== enableTimeSelection || $[49] !== handleSelect || $[50] !== handleTimeChange || $[51] !== localDisplayRange || $[52] !== t17 || $[53] !== t18 || $[54] !== t20) {
    t21 = <PopoverContent className="w-auto p-0 z-51" align="end"><Calendar initialFocus={true} defaultMonth={t17} selected={localDisplayRange} onSelect={handleSelect} numberOfMonths={2} fromDate={t18} toDate={t19} enableTimeSelection={enableTimeSelection} onTimeChange={handleTimeChange} timeRange={t20} /></PopoverContent>;
    $[48] = enableTimeSelection;
    $[49] = handleSelect;
    $[50] = handleTimeChange;
    $[51] = localDisplayRange;
    $[52] = t17;
    $[53] = t18;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t16 || $[57] !== t21) {
    t22 = <Popover>{t16}{t21}</Popover>;
    $[56] = t16;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== props || $[60] !== t10 || $[61] !== t22) {
    t23 = <div className={t10} {...props}>{t22}</div>;
    $[59] = props;
    $[60] = t10;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  return t23;
}